import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";

export default function Vote() {
  const { t, pick } = useI18n();
  const [questions, setQuestions] = useState([]);
  const [myVotes, setMyVotes] = useState({});
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [q, mv, r] = await Promise.all([
          api.get("/questions"), api.get("/vote/mine"), api.get("/results"),
        ]);
        setQuestions(q.data.questions);
        setMyVotes(mv.data.votes || {});
        setResults(r.data.results);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F4F4]" data-testid="vote-loading">
        <div className="font-mono-civic text-sm uppercase tracking-widest text-black">{t("vote_loading")}</div>
      </div>
    );
  }

  const answeredCount = Object.keys(myVotes).length;
  const total = questions.length;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black px-5 py-10" data-testid="vote-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black">{t("vote_ballot_tag")}</div>
            <h1 className="mt-2 font-serif-display text-black leading-[0.9] text-5xl sm:text-6xl" style={{ fontWeight: 800 }}>{t("vote_h1")}</h1>
            <p className="mt-3 text-black max-w-xl" style={{ fontWeight: 700 }}>{t("vote_intro")}</p>
          </div>
          <div className="text-right">
            <div className="font-mono-civic text-xs uppercase tracking-widest text-black">{t("vote_answered")}</div>
            <div className="font-serif-display text-3xl text-black" style={{ fontWeight: 800 }} data-testid="vote-progress">
              {answeredCount} / {total}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 border-2 border-[#E00000] bg-white p-3 text-[#E00000] text-sm" style={{ fontWeight: 800 }} data-testid="vote-error">{error}</div>
        )}

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {questions.map((q, idx) => {
            const r = results.find((x) => x.id === q.id);
            const choice = myVotes[q.id];
            const yes = r?.yes ?? 0;
            const no = r?.no ?? 0;
            const text = pick(q, "text");
            return (
              <Link key={q.id} to={`/vote/${q.id}`}
                className={`block bg-white border-2 border-black p-6 ${q.main ? "brutal-shadow-red sm:col-span-2" : "brutal-shadow-sm"} hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all`}
                data-testid={`vote-card-link-${q.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black">
                    {t("vote_q_label")} {String(idx + 1).padStart(2, "0")}
                  </div>
                  {q.main && (
                    <span className="font-mono-civic text-[10px] uppercase tracking-widest bg-[#E00000] text-white px-2 py-1" style={{ fontWeight: 800 }}>
                      {t("vote_headline_tag")}
                    </span>
                  )}
                </div>
                <p className={`font-serif-display text-black leading-[1.08] ${q.main ? "text-2xl sm:text-3xl italic" : "text-xl sm:text-2xl"}`} style={{ fontWeight: 800 }}>
                  {text}
                </p>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="border-2 border-black bg-white px-3 py-2 text-center">
                    <div className="font-mono-civic text-[10px] uppercase tracking-widest text-black">{t("vote_card_yes")}</div>
                    <div className="font-serif-display text-2xl text-black leading-none mt-1" style={{ fontWeight: 800 }} data-testid={`vote-card-yes-${q.id}`}>
                      {yes.toLocaleString()}
                    </div>
                  </div>
                  <div className="border-2 border-black bg-white px-3 py-2 text-center">
                    <div className="font-mono-civic text-[10px] uppercase tracking-widest text-black">{t("vote_card_no")}</div>
                    <div className="font-serif-display text-2xl text-[#E00000] leading-none mt-1" style={{ fontWeight: 800 }} data-testid={`vote-card-no-${q.id}`}>
                      {no.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between font-mono-civic text-[10px] uppercase tracking-widest text-black">
                  <span>
                    {t("vote_your_vote")} {choice ? (
                      <span className="text-[#E00000]" style={{ fontWeight: 900 }}>{choice === "YES" ? t("q_yes") : t("q_no")}</span>
                    ) : <span className="opacity-60">{t("vote_not_yet")}</span>}
                  </span>
                  <span style={{ fontWeight: 900 }}>{t("vote_open")}</span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 border-t-2 border-black pt-8 flex flex-col sm:flex-row gap-4">
          <Link to="/results"
            className="inline-flex items-center justify-center bg-black text-white uppercase tracking-widest px-8 py-5 border-2 border-black brutal-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex-1 sm:flex-initial"
            style={{ fontWeight: 800 }} data-testid="vote-see-results-btn">
            {t("vote_overview")}
          </Link>
          <Link to="/home"
            className="inline-flex items-center justify-center bg-white text-black uppercase tracking-widest px-8 py-5 border-2 border-black hover:bg-black hover:text-white transition-colors flex-1 sm:flex-initial"
            style={{ fontWeight: 800 }}>
            {t("vote_back_home")}
          </Link>
        </div>
      </div>
    </div>
  );
}
